/**
 * ParentEntityContext provides a flexible way to establish parent-child relationships
 * between components in a React tree. While initially used for learning pathways and modules,
 * it's designed to be generic enough for any parent-child relationship in the application.
 *
 * Key Features:
 * 1. Generic Parent-Child Relationships
 *    - Not tied to specific component types
 *    - Can be used for any hierarchical data structure
 *    - Extensible through the EntityType enum
 *
 * 2. Context Nesting
 *    - Each Provider creates its own context scope
 *    - Components using useParentEntityContext() will access the nearest parent Provider
 *    - Multiple Providers can be nested to create deep hierarchies
 *
 * 3. Component Reusability
 *    - Same component can be used in different parent contexts
 *    - Parent information is dynamically provided based on where component is rendered
 *
 * @example
 * // Multiple instances of the same component can have different parent contexts
 * function App() {
 *   return (
 *     <>
 *       <ParentEntityProvider
 *         parentId="pathway-1"
 *         parentType={EntityType.LearningPathway}
 *       >
 *         <LearningModule /> // This instance will see pathway-1 as parent
 *       </ParentEntityProvider>
 *
 *       <ParentEntityProvider
 *         parentId="pathway-2"
 *         parentType={EntityType.LearningPathway}
 *       >
 *         <LearningModule /> // This instance will see pathway-2 as parent
 *       </ParentEntityProvider>
 *     </>
 *   );
 * }
 *
 * @example
 * // Nested contexts demonstrate hierarchical relationships
 * function ComplexHierarchy() {
 *   return (
 *     <ParentEntityProvider
 *       parentId="course-1"
 *       parentType={EntityType.Course}
 *     >
 *       <CourseContent>
 *         <ParentEntityProvider
 *           parentId="pathway-1"
 *           parentType={EntityType.LearningPathway}
 *         >
 *           <LearningModule /> // Will see pathway-1 as immediate parent
 *         </ParentEntityProvider>
 *       </CourseContent>
 *     </ParentEntityProvider>
 *   );
 * }
 */

import React, { createContext, useContext, useMemo } from 'react';
import { EntityType } from 'src/constants/entity';

/**
 * The shape of the context value provided by ParentEntityContext
 */
interface ParentEntityContextType {
  /**
   * Unique identifier of the parent entity (e.g., a learning pathway ID)
   */
  parentId?: string;

  /**
   * Type of the parent entity (e.g., LEARNING_PATHWAY)
   * @see EntityType enum for possible values
   */
  parentType?: EntityType;
}

const ParentEntityContext = createContext<ParentEntityContextType>({});

/**
 * Hook to access the parent entity context values
 * @returns ParentEntityContextType containing parentId and parentType
 *
 * @example
 * function MyComponent() {
 *   const { parentId, parentType } = useParentEntityContext();
 *   // Use context values...
 * }
 */
export const useParentEntityContext = () => useContext(ParentEntityContext);

/**
 * Provider component that makes parent entity information available to its children
 *
 * @param props.parentId - Unique identifier of the parent entity
 * @param props.parentType - Type of the parent entity from EntityType enum
 * @param props.children - React child components that will have access to the context
 */
export const ParentEntityProvider: React.FC<{
  children: React.ReactNode;
  parentId?: string;
  parentType?: EntityType;
}> = ({ children, parentId, parentType }) => {
  const value = useMemo(() => ({ parentId, parentType }), [parentId, parentType]);

  return (
    <ParentEntityContext.Provider value={value}>
      {children}
    </ParentEntityContext.Provider>
  );
};
